import { useMutation } from "@apollo/client";
import { Stack, TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import pluralize from "pluralize";
import { useCallback, useState } from "react";

import { BULK_SET_CI_NOTES } from "../api";
import { useEpcraBulkActionContext } from "./bulkActionContext";

const REFETCH = ["CredentialImplementationsForStaff", "CredentialImplementationForStaff"] as const;

export function BulkUpdateNotesDialog() {
  const alerts = useAlerts();
  const { dialogState, onClose, onSuccess, selectedRows } = useEpcraBulkActionContext();
  const [notes, setNotes] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [mutate, { loading }] = useMutation(BULK_SET_CI_NOTES, {
    refetchQueries: [...REFETCH],
  });

  const reset = useCallback(() => {
    setNotes("");
    setConfirmOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleSubmit = useCallback(async () => {
    const ids = selectedRows.map((r) => r.id);
    try {
      const { data } = await mutate({
        variables: { ids, notes },
      });
      alerts.success(
        `Updated notes on ${data?.bulkSetCredentialImplementationNotes.updated ?? 0} ${pluralize("record", data?.bulkSetCredentialImplementationNotes.updated ?? 0)}`
      );
      handleClose();
      onSuccess();
    } catch (e) {
      alerts.error("Could not update notes", e);
    }
  }, [alerts, handleClose, mutate, notes, onSuccess, selectedRows]);

  const open = dialogState === "notes";

  return (
    <>
      <Dialog
        open={open && !confirmOpen}
        onClose={handleClose}
        title={`Update notes (${selectedRows.length} selected)`}
        description={
          <Typography variant="body2" color="text.secondary" component="span">
            This overwrites the notes field on every selected record with the text below (separate
            from the per-row comment thread).
          </Typography>
        }
        primaryActionButtonProps={{
          onClick: () => setConfirmOpen(true),
          disabled: !notes.trim(),
          saveText: "Continue",
        }}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            size="small"
          />
        </Stack>
      </Dialog>
      <ConfirmDialog
        open={open && confirmOpen}
        onClose={() => setConfirmOpen(false)}
        header="Overwrite notes?"
        info={`This will replace notes on ${selectedRows.length} ${pluralize("record", selectedRows.length)}. This cannot be undone from here.`}
        confirmText="Overwrite notes"
        cancelText="Back"
        loading={loading}
        onConfirm={() => handleSubmit()}
      />
    </>
  );
}
